import IParamScript from "../database/interfaces/paramScript/IParamScript";
import IOrder from "../unityView/interfaces/IOrder";

declare const _UC_ZERO_UUID_: string;
declare const _UC_ENV_: string;

/**
 * This must be in sync with the UCC.php in ubercart module
 */
class CartItem {
    public id_cabinet: number = -1;
    public cartAction: string = "";
    public Name: string = "";
    public productImage?: string;
    public count: number = 0;
    public Price: number = 0;
    public PriceGross: number = 0;
    public priceFinal: boolean = false;
    public Dimensions: string = "";
    public children: IOrder[] = []; // Order items collected from Unity
    public uconScript: any[] = []; // We do not use the uconScript in the configurator, so leaving it as any[]
    public descriptor: IParamScript[] = [];
    // Admin script design (quote): the StyleScript Unity built it from; null = from the descriptor
    public styleScript: any[] | null = null;

    public hash?: number;

    public static getHash(item: CartItem): number {
        if (item.hash !== undefined) {
            return item.hash;
        }

        // A kosár-azonosságot a geometria/konfiguráció ÉS a gyökér név EGYÜTT adja: azonos
        // méretű/dekorú, de eltérő nevű elem külön sorként kerül a kosárba. A nevet trimmeljük,
        // de kis-/nagybetű-érzékenyen (exact) hasonlítjuk. A descriptor Name-bejegyzését
        // kivesszük (annak értéke az egyediesítés előtti, így megbízhatatlan), és helyette a
        // látható, már egyediesített item.Name-et fűzzük a hashhez.
        const descriptorForHash = item.descriptor.filter(d => {
            const isNameEntry =
                d.parameterProperty === "Name" &&
                d.targetPath === "" &&
                d.targetType === null;

            return !isNameEntry;
        });

        // A line whose descriptor is the name alone (an AI design, see CartHandler.createNameDescriptor)
        // is told apart from another design of the same name by its ucon.
        const uconForHash = descriptorForHash.length === 0
            ? "|ucon=" + JSON.stringify(item.uconScript) + "|style=" + JSON.stringify(item.styleScript ?? null)
            : "";
        const hashString: string = JSON.stringify(descriptorForHash) + "|name=" + item.Name.trim() + uconForHash;
        // Simple hash function from https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript
        let hash: number = 0;
        for (const char of hashString) {
            hash = (hash << 5) - hash + char.charCodeAt(0);
            hash |= 0; // Constrain to 32bit integer
        }
        item.hash = hash;
        return hash;
    }
}

class RemainderItem extends CartItem {
    public isRemovable: boolean = false;
    public unusedArea: number = 0;
    public image?: string;

    constructor() {
        super();
        this.count = 1;
    }
}

/** One bag line of a hardware plan entry (UCC.php buildHardwarePlan). */
interface IHardwareBag {
    pack_reference: string;
    pack_size: number;
    qty: number;
    name: string;
    price: number;
    priceGross: number;
}

/** Cart-wide hardware plan entry: needed pieces of one piece reference -> bags. */
interface IHardwarePlanEntry {
    piece_reference: string;
    name: string;
    needed: number;
    supplied: number;
    leftover: number;
    bags: IHardwareBag[];
    total: number;
    totalGross: number;
    error: string | null;
    image?: string | null;
    /** false = sold per piece -> priced on the cabinet child line, not in the summary. */
    packed?: boolean;
}

class HardwareItem extends CartItem {
    public pieceRef: string = "";
    public needed: number = 0;
    public supplied: number = 0;
    public leftover: number = 0;
    public bags: IHardwareBag[] = [];
    public error: string | null = null;
    public packed: boolean = true;

    constructor() {
        super();
        this.count = 1;
    }
}

// Derived summary lines for bagged hardware. PriceGross = bag total, count = 1.
// Per-piece hardware (packed: false) stays on the cabinet child lines.
const createHardwareItemsFromPlan = (plan: IHardwarePlanEntry[] | null | undefined): HardwareItem[] => {
    if (!Array.isArray(plan)) return [];
    return plan.filter(entry => entry.packed !== false).map(entry => {
        const item = new HardwareItem();
        item.pieceRef = entry.piece_reference;
        item.Name = entry.name || entry.piece_reference;
        item.needed = entry.needed;
        item.supplied = entry.supplied;
        item.leftover = entry.leftover;
        item.bags = entry.bags ?? [];
        item.error = entry.error;
        item.Price = entry.total || 0;
        item.PriceGross = entry.totalGross || 0;
        item.productImage = entry.image || undefined;
        return item;
    });
};

// A cabinet is any cart line that is NOT a derived display line (remainder, hardware).
const isCabinetItem = (ci: CartItem): boolean => !(ci instanceof RemainderItem) && !(ci instanceof HardwareItem);

// Aggregate one cabinet's children Hardwares into sub-lines (piece count per one cabinet
// unit), keyed by hardware ProductID. Per-piece hardware gets its unit price from the plan;
// bagged hardware has no price here (summary line).
const aggregateCabinetHardware = (cabinet: CartItem, plan?: IHardwarePlanEntry[] | null): HardwareItem[] => {
    if (_UC_ENV_ !== "local" || !isCabinetItem(cabinet)) return [];
    const planByRef = new Map((plan ?? []).map(entry => [entry.piece_reference, entry]));
    const byKey = new Map<string, HardwareItem>();
    for (const child of (cabinet.children ?? [])) {
        for (const hw of (child.Hardwares ?? [])) {
            if (!hw.ProductID || hw.ProductID === _UC_ZERO_UUID_) continue;
            const view = hw.ViewData;
            const key = hw.ProductID;
            let line = byKey.get(key);
            if (!line) {
                const entry = view ? planByRef.get(view.ps_product_reference) : undefined;
                line = new HardwareItem();
                line.Name = view?.name || hw.ProductName || "Vasalat";
                line.productImage = view?.image || undefined;
                line.count = 0;
                line.packed = entry?.packed !== false;
                line.error = entry?.error ?? null;
                line.PriceGross = !line.packed && !line.error ? (entry?.bags[0]?.priceGross ?? 0) : 0;
                byKey.set(key, line);
            }
            line.count += 1;
        }
    }
    return [...byKey.values()];
};

export default CartItem;
export { RemainderItem, HardwareItem, isCabinetItem, aggregateCabinetHardware, createHardwareItemsFromPlan };
export type { IHardwareBag, IHardwarePlanEntry };
